import type { UserInfo } from '@/entities/User/model/types'

const JIVO_WIDGET_ID = 'h7Z3n0l44b'
const JIVO_SCRIPT_SRC = `https://code.jivo.ru/widget/${JIVO_WIDGET_ID}`
const JIVO_FAB_ID = 'app-jivo-fab'
const JIVO_HIDE_DEFAULT_STYLES_ID = 'jivo-hide-default-label'

type JivoContactInfo = {
  name?: string
  email?: string
  phone?: string
  description?: string
}

type JivoApi = {
  open: (params?: { start?: 'chat' | 'call' | 'menu' }) => { result: string }
  close: () => void
  setContactInfo: (info: JivoContactInfo) => void
  setUserToken: (token: string) => void
}

type JivoUserContactPayload = {
  contact: JivoContactInfo
  token: string
}

declare global {
  interface Window {
    jivo_api?: JivoApi
    jivo_onLoadCallback?: () => void
    jivo_onOpen?: () => void
    jivo_onClose?: () => void
  }
}

let isGuestMode = true
let openChatWhenReady = false
let callbacksRegistered = false
let pendingContact: JivoUserContactPayload | null = null

/**
 * Скрываем только штатный ярлык Jivo (не ломая его размеры).
 * @see https://www.jivo.ru/docs/widget/
 * @see https://www.jivo.ru/help/customization/sozdaem-svoi-iarlyk-onlain-konsultanta.html
 */
function hideDefaultJivoLabel(): void {
  if (document.getElementById(JIVO_HIDE_DEFAULT_STYLES_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = JIVO_HIDE_DEFAULT_STYLES_ID
  style.textContent = `
    #jvLabelWrap,
    .__jivoMobileButton {
      display: none !important;
    }
  `
  document.head.appendChild(style)
}

function destroyJivoFab(): void {
  document.getElementById(JIVO_FAB_ID)?.remove()
}

function syncFabVisibility(): void {
  if (!isGuestMode) {
    destroyJivoFab()
    return
  }

  createJivoFab()
}

function createJivoFab(): HTMLButtonElement {
  const existing = document.getElementById(JIVO_FAB_ID)
  if (existing instanceof HTMLButtonElement) {
    existing.style.display = 'inline-flex'
    existing.hidden = false
    return existing
  }

  const fab = document.createElement('button')
  fab.id = JIVO_FAB_ID
  fab.type = 'button'
  fab.setAttribute('aria-label', 'Открыть чат поддержки')
  fab.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v8A2.5 2.5 0 0 1 17.5 17H9l-4 3v-3.5A2.5 2.5 0 0 1 4 14.5v-8Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
    </svg>
  `

  fab.style.cssText = `
    position: fixed;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #151515;
    background: linear-gradient(90deg, #0FEF66 0%, #03D0FB 100%);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    cursor: pointer;
    z-index: 2147483000;
    -webkit-tap-highlight-color: transparent;
  `

  fab.addEventListener('click', () => {
    openJivoChat()
  })

  document.body.appendChild(fab)
  return fab
}

function buildUserContactPayload(user: UserInfo): JivoUserContactPayload {
  const login = user.login?.trim()
  const username = user.username?.trim()
  const email = user.email?.trim()

  const contact: JivoContactInfo = {
    name: login || username || undefined,
    description: `tg_id: ${user.tg_id}`,
  }

  if (email) {
    contact.email = email
  }

  return {
    contact,
    token: String(user.tg_id),
  }
}

function applyContactPayload(payload: JivoUserContactPayload): boolean {
  const api = window.jivo_api
  if (!api) {
    return false
  }

  api.setContactInfo(payload.contact)
  api.setUserToken(payload.token)
  return true
}

function flushPendingContact(): void {
  if (!pendingContact) {
    return
  }

  if (applyContactPayload(pendingContact)) {
    pendingContact = null
  }
}

function tryOpenJivoChat(): boolean {
  const api = window.jivo_api
  if (!api) {
    return false
  }

  if (pendingContact) {
    applyContactPayload(pendingContact)
  }

  api.open({ start: 'chat' })
  return true
}

function onJivoLoaded(): void {
  hideDefaultJivoLabel()
  syncFabVisibility()
  flushPendingContact()

  if (openChatWhenReady) {
    openChatWhenReady = false
    tryOpenJivoChat()
  }
}

function registerJivoCallbacks(): void {
  if (callbacksRegistered) {
    return
  }
  callbacksRegistered = true

  window.jivo_onLoadCallback = () => {
    onJivoLoaded()
  }

  window.jivo_onOpen = () => {
    destroyJivoFab()
  }

  window.jivo_onClose = () => {
    // FAB только для гостей; у авторизованных вход через «Поддержка»
    if (isGuestMode) {
      createJivoFab()
    }
  }
}

/**
 * Передаёт логин/email в Jivo через setContactInfo / setUserToken.
 * @see https://www.jivo.ru/docs/widget/#setcontactinfo
 */
export function syncJivoUserContact(user: UserInfo | null): void {
  if (!user) {
    pendingContact = null
    return
  }

  const payload = buildUserContactPayload(user)
  pendingContact = payload

  if (applyContactPayload(payload)) {
    pendingContact = null
  }
}

export function setJivoGuestMode(isGuest: boolean): void {
  isGuestMode = isGuest
  hideDefaultJivoLabel()
  syncFabVisibility()
}

export function openJivoChat(): void {
  if (tryOpenJivoChat()) {
    return
  }

  openChatWhenReady = true
  initJivoWidget()
}

export function initJivoWidget(): void {
  registerJivoCallbacks()
  hideDefaultJivoLabel()

  if (document.querySelector(`script[src="${JIVO_SCRIPT_SRC}"]`)) {
    if (window.jivo_api) {
      onJivoLoaded()
    }
    return
  }

  const script = document.createElement('script')
  script.src = JIVO_SCRIPT_SRC
  script.async = true
  document.body.appendChild(script)
}
